import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getProjectBySlug, getNextProject } from "../data/projects";
import "./ProjectDetail.css";

/* ── Per-section visuals for the pinned panel ── */
// Overview: a clean phone mockup of Siya on a live call — avatar, name, a live
// timer, an animated waveform and call controls.
const OrbitVisual = () => (
  <svg className="cs-pv-svg cs-phone" viewBox="0 0 200 272">
    <defs>
      <linearGradient id="csPhoneScreen" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" />
        <stop offset="1" stopColor="#eeebe6" />
      </linearGradient>
    </defs>
    <circle cx="100" cy="136" r="94" className="cs-call-glow" />
    <g className="cs-phone-float">
      {/* frame + screen */}
      <rect x="44" y="12" width="112" height="248" rx="26" className="cs-phone-frame" />
      <rect x="52" y="20" width="96" height="232" rx="18" fill="url(#csPhoneScreen)" className="cs-phone-screen" />
      <rect x="90" y="28" width="20" height="3.5" rx="1.75" className="cs-phone-notch" />

      {/* live status */}
      <circle cx="70" cy="45.5" r="2.3" className="cs-call-live" />
      <text x="78" y="48.5" className="cs-phone-status">ON CALL · 00:12</text>

      {/* avatar */}
      <circle cx="100" cy="92" r="26" className="cs-phone-avring cs-hub-ring" />
      <circle cx="100" cy="92" r="22" className="cs-phone-av" />
      <text x="100" y="99" textAnchor="middle" className="cs-phone-avtext">S</text>

      {/* name */}
      <text x="100" y="136" textAnchor="middle" className="cs-phone-name">Siya</text>
      <text x="100" y="151" textAnchor="middle" className="cs-phone-sub">AI VOICE AGENT</text>

      {/* waveform */}
      {[9, 15, 22, 15, 9].map((h, i) => (
        <rect
          key={i}
          x={82 + i * 8}
          y={178 - h / 2}
          width="3.4"
          height={h}
          rx="1.7"
          className="cs-voice-bar"
          style={{ animationDelay: `${i * 0.12}s` }}
        />
      ))}

      {/* call controls */}
      <circle cx="71" cy="220" r="13" className="cs-phone-btn" />
      <g transform="translate(71 220) scale(0.72) translate(-71 -220)">
        <rect x="69" y="215" width="4" height="8" rx="2" className="cs-phone-glyph" />
        <path d="M67 223.5 a4 4 0 0 0 8 0" className="cs-phone-glyph-s" />
      </g>

      <circle cx="129" cy="220" r="13" className="cs-phone-btn" />
      <g transform="translate(129 220) scale(0.72) translate(-129 -220)">
        <path d="M124 216 l4 0 l4 -3 v14 l-4 -3 l-4 0 z" className="cs-phone-glyph" />
        <path d="M135 216 a5 5 0 0 1 0 8" className="cs-phone-glyph-s" />
      </g>

      <circle cx="100" cy="220" r="14.5" className="cs-phone-btn cs-phone-btn--end" />
      <g transform="translate(100 220) rotate(135) scale(0.46) translate(-12 -12)">
        <path
          d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
          className="cs-phone-endpath"
        />
      </g>
    </g>
  </svg>
);

// Signal-transformation flow: the voice changes form as it moves down the
// pipeline — waveform (you) → text (transcript) → spark (LLM) → waveform (Siya).
// A comet flows down the spine and each stage ignites as it passes.
const tfRep = (type) => {
  if (type === "wave") {
    const hs = [9, 16, 23, 15, 10];
    return (
      <g className="cs-tf-wave">
        {hs.map((h, j) => (
          <rect
            key={j}
            x={-12 + j * 6}
            y={-h / 2}
            width="3"
            height={h}
            rx="1.5"
            className="cs-tf-wavebar"
            style={{ animationDelay: `${j * 0.1}s` }}
          />
        ))}
      </g>
    );
  }
  if (type === "text") {
    return (
      <g>
        <rect x="-13" y="-7" width="26" height="2.6" rx="1.3" className="cs-tf-line" />
        <rect x="-13" y="-1" width="20" height="2.6" rx="1.3" className="cs-tf-line" />
        <rect x="-13" y="5" width="14" height="2.6" rx="1.3" className="cs-tf-line" />
      </g>
    );
  }
  return (
    <g className="cs-tf-spark">
      <path d="M0 -14 V14 M-14 0 H14 M-8 -8 L8 8 M-8 8 L8 -8" className="cs-tf-sparkline" />
      <circle r="2.6" className="cs-pv-solid" />
    </g>
  );
};

// Overview fallback for every other project: a neutral stack of product
// panels. The phone mockup above is specific to Siya, so it must not stand in
// as the generic overview visual.
const LayersVisual = () => (
  <svg className="cs-pv-svg cs-layers" viewBox="0 0 240 252">
    <circle cx="120" cy="126" r="82" className="cs-layers-glow" />
    {[0, 1, 2].map((i) => (
      <g key={i} className="cs-layers-card" style={{ animationDelay: `${i * 0.5}s` }}>
        <rect
          x="48"
          y={40 + i * 58}
          width="144"
          height="48"
          rx="12"
          className="cs-layers-rect"
        />
        <rect
          x="62"
          y={56 + i * 58}
          width={64 - i * 8}
          height="6"
          rx="3"
          className="cs-layers-bar"
        />
        <rect
          x="62"
          y={69 + i * 58}
          width={100 - i * 14}
          height="4"
          rx="2"
          className="cs-layers-bar cs-layers-bar--dim"
        />
        <circle cx="176" cy={64 + i * 58} r="4" className="cs-layers-dot" />
      </g>
    ))}
  </svg>
);

/* ── Per-module visuals (IAS Sathi) ── */

// RAG mentor: NCERT sources retrieved into a grounded, structured answer.
const RagVisual = () => (
  <svg className="cs-pv-svg cs-rag" viewBox="0 0 240 252">
    {[0, 1, 2].map((i) => (
      <g key={i} className="cs-rag-doc" style={{ animationDelay: `${i * 0.25}s` }}>
        <rect x={30 + i * 60} y="12" width="42" height="52" rx="7" className="cs-rag-docbox" />
        <rect x={38 + i * 60} y="24" width="26" height="4" rx="2" className="cs-rag-docline" />
        <rect x={38 + i * 60} y="34" width="18" height="3.5" rx="1.75" className="cs-rag-docline cs-rag-docline--dim" />
        <rect x={38 + i * 60} y="43" width="22" height="3.5" rx="1.75" className="cs-rag-docline cs-rag-docline--dim" />
      </g>
    ))}
    <text x="120" y="80" textAnchor="middle" className="cs-rag-cap">NCERT · updated 2025</text>

    {[0, 1, 2].map((i) => (
      <line
        key={i}
        x1={51 + i * 60}
        y1="88"
        x2="120"
        y2="112"
        className="cs-rag-wire"
        style={{ animationDelay: `${i * 0.3}s` }}
      />
    ))}

    <circle cx="120" cy="128" r="18" className="cs-rag-hub" />
    <text x="120" y="132" textAnchor="middle" className="cs-rag-hubtext">RAG</text>

    {/* grounded answer, with a chart drawn inside it */}
    <rect x="30" y="162" width="180" height="78" rx="11" className="cs-rag-ans" />
    <rect x="42" y="174" width="68" height="5" rx="2.5" className="cs-rag-ansline" />
    {[16, 27, 21, 33].map((h, i) => (
      <rect
        key={i}
        x={42 + i * 17}
        y={228 - h}
        width="11"
        height={h}
        rx="2.5"
        className="cs-rag-bar"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
    <rect x="128" y="196" width="70" height="4" rx="2" className="cs-rag-ansline cs-rag-ansline--dim" />
    <rect x="128" y="208" width="56" height="4" rx="2" className="cs-rag-ansline cs-rag-ansline--dim" />
    <rect x="128" y="220" width="64" height="4" rx="2" className="cs-rag-ansline cs-rag-ansline--dim" />
  </svg>
);

// Adaptive MCQs: options resolve to the right answer; history means no repeats.
const McqVisual = () => (
  <svg className="cs-pv-svg cs-mcq" viewBox="0 0 240 252">
    <rect x="24" y="14" width="192" height="48" rx="10" className="cs-mcq-q" />
    <rect x="38" y="28" width="92" height="6" rx="3" className="cs-mcq-qline" />
    <rect x="38" y="42" width="140" height="4.5" rx="2.25" className="cs-mcq-qline cs-mcq-qline--dim" />

    {[0, 1, 2, 3].map((i) => (
      <g
        key={i}
        className={`cs-mcq-opt ${i === 2 ? "is-right" : ""}`}
        style={{ animationDelay: `${i * 0.16}s` }}
      >
        <rect x="24" y={76 + i * 36} width="192" height="28" rx="8" className="cs-mcq-optbox" />
        <circle cx="43" cy={90 + i * 36} r="7" className="cs-mcq-radio" />
        <rect x="60" y={87 + i * 36} width={118 - i * 16} height="5" rx="2.5" className="cs-mcq-optline" />
        {i === 2 && (
          <path d={`M39.5 ${90 + i * 36} l3 3 l5.5 -6`} className="cs-mcq-check" />
        )}
      </g>
    ))}

    <text x="120" y="238" textAnchor="middle" className="cs-mcq-cap">
      history tracked · never repeated
    </text>
  </svg>
);

// Answer evaluation: handwriting scanned by OCR, then scored by structure.
const OcrVisual = () => (
  <svg className="cs-pv-svg cs-ocr" viewBox="0 0 240 252">
    <rect x="46" y="10" width="148" height="146" rx="10" className="cs-ocr-paper" />
    {[0, 1, 2, 3, 4, 5].map((i) => (
      <path
        key={i}
        d={`M60 ${32 + i * 21} q 11 -6 22 0 t 22 0 t 22 0 t ${i % 2 ? 16 : 28} 0`}
        className="cs-ocr-ink"
        style={{ animationDelay: `${i * 0.12}s` }}
      />
    ))}
    <rect x="46" y="10" width="148" height="3" className="cs-ocr-scan" />

    {[["Intro", 0], ["Body", 1], ["Concl.", 2]].map(([t, i]) => (
      <g key={t} className="cs-ocr-score" style={{ animationDelay: `${1 + i * 0.2}s` }}>
        <rect x={20 + i * 70} y="178" width="62" height="50" rx="9" className="cs-ocr-pill" />
        <text x={51 + i * 70} y="200" textAnchor="middle" className="cs-ocr-pilltext">{t}</text>
        <rect x={32 + i * 70} y="209" width="38" height="4" rx="2" className="cs-ocr-pillbar" />
        <rect x={32 + i * 70} y="217" width={26 - i * 5} height="4" rx="2" className="cs-ocr-pillbar cs-ocr-pillbar--dim" />
      </g>
    ))}
    <text x="120" y="244" textAnchor="middle" className="cs-ocr-cap">~90% OCR accuracy</text>
  </svg>
);

// Live lectures: a generated slide with explanation, chart and imagery.
const LectureVisual = () => (
  <svg className="cs-pv-svg cs-lec" viewBox="0 0 240 252">
    <rect x="22" y="30" width="196" height="140" rx="11" className="cs-lec-slide" />
    <rect x="36" y="46" width="76" height="7" rx="3.5" className="cs-lec-title" />
    <rect x="36" y="61" width="52" height="4" rx="2" className="cs-lec-title cs-lec-title--dim" />

    {[28, 46, 36, 60, 50].map((h, i) => (
      <rect
        key={i}
        x={36 + i * 19}
        y={152 - h}
        width="13"
        height={h}
        rx="3"
        className="cs-lec-bar"
        style={{ animationDelay: `${i * 0.14}s` }}
      />
    ))}

    <rect x="146" y="72" width="58" height="46" rx="7" className="cs-lec-img" />
    <circle cx="160" cy="86" r="5" className="cs-lec-imgsun" />
    <path d="M148 116 l15 -17 l11 12 l9 -8 l19 13 z" className="cs-lec-imghill" />

    {[0, 1, 2].map((i) => (
      <circle
        key={i}
        cx={106 + i * 14}
        cy="188"
        r="4"
        className="cs-lec-dot"
        style={{ animationDelay: `${i * 0.35}s` }}
      />
    ))}
    <text x="120" y="222" textAnchor="middle" className="cs-lec-cap">any topic → a taught lecture</text>
  </svg>
);

/* ── Two-stage visuals (Answer Evaluation) ── */

// Stage 1: pages rasterised, OCR'd in parallel, split into question/answer pairs.
const ExtractVisual = () => (
  <svg className="cs-pv-svg cs-ex" viewBox="0 0 240 252">
    {/* stacked pages, back to front */}
    {[2, 1, 0].map((i) => (
      <rect
        key={i}
        x={62 - i * 9}
        y={4 + i * 6}
        width="116"
        height="98"
        rx="8"
        className={`cs-ex-page ${i === 0 ? "cs-ex-page--front" : ""}`}
      />
    ))}
    {[0, 1, 2, 3].map((i) => (
      <path
        key={i}
        d={`M76 ${28 + i * 19} q 10 -6 20 0 t 20 0 t ${i % 2 ? 14 : 24} 0`}
        className="cs-ex-ink"
        style={{ animationDelay: `${i * 0.12}s` }}
      />
    ))}
    <rect x="62" y="4" width="116" height="3" className="cs-ex-scan" />
    <text x="120" y="128" textAnchor="middle" className="cs-ex-cap">
      pages OCR'd in parallel
    </text>

    <path d="M120 136 v12" className="cs-ex-arrow" />
    <path d="M116 145 l4 5 l4 -5" className="cs-ex-arrow" />

    {/* structured question / answer output */}
    <rect x="24" y="156" width="192" height="84" rx="10" className="cs-ex-card" />
    <rect x="36" y="166" width="17" height="14" rx="4" className="cs-ex-chip" />
    <text x="44.5" y="176.5" textAnchor="middle" className="cs-ex-chiptext">Q</text>
    <rect x="60" y="170" width="108" height="5" rx="2.5" className="cs-ex-line" />

    <rect x="36" y="192" width="17" height="14" rx="4" className="cs-ex-chip" />
    <text x="44.5" y="202.5" textAnchor="middle" className="cs-ex-chiptext">A</text>
    <rect x="60" y="193" width="140" height="4" rx="2" className="cs-ex-line cs-ex-line--dim" />
    <rect x="60" y="203" width="118" height="4" rx="2" className="cs-ex-line cs-ex-line--dim" />
    <rect x="60" y="213" width="132" height="4" rx="2" className="cs-ex-line cs-ex-line--dim" />
    <rect x="60" y="223" width="96" height="4" rx="2" className="cs-ex-line cs-ex-line--dim" />
  </svg>
);

// Stage 2: the extracted answer scored across the rubric, dimension by dimension.
const EV_ROWS = [104, 86, 116, 70, 96, 78];
const EvaluateVisual = () => (
  <svg className="cs-pv-svg cs-ev" viewBox="0 0 240 252">
    {EV_ROWS.map((w, i) => (
      <g key={i}>
        <rect x="22" y={22 + i * 29} width={48 - (i % 3) * 6} height="5" rx="2.5" className="cs-ev-label" />
        <rect x="86" y={17 + i * 29} width="126" height="11" rx="5.5" className="cs-ev-track" />
        <rect
          x="86"
          y={17 + i * 29}
          width={w}
          height="11"
          rx="5.5"
          className="cs-ev-fill"
          style={{ animationDelay: `${i * 0.13}s` }}
        />
      </g>
    ))}
    <rect x="22" y="200" width="190" height="38" rx="10" className="cs-ev-total" />
    <text x="38" y="223" className="cs-ev-totallabel">TOTAL</text>
    <text x="196" y="225" textAnchor="end" className="cs-ev-totalvalue">7.5</text>
  </svg>
);

const MODULE_VISUALS = {
  rag: RagVisual,
  mcq: McqVisual,
  ocr: OcrVisual,
  lecture: LectureVisual,
  extract: ExtractVisual,
  evaluate: EvaluateVisual,
};

const TF_STAGES = [
  { type: "wave", label: "You speak" },
  { type: "text", label: "Transcribed" },
  { type: "ai", label: "GPT-4o-mini" },
  { type: "wave", label: "Siya replies" },
];
const TF_YS = [48, 118, 188, 258];
const FlowVisual = () => {
  const cx = 92;
  return (
    <svg className="cs-pv-svg cs-tf" viewBox="0 0 250 306">
      <line x1={cx} y1="48" x2={cx} y2="258" className="cs-tf-track" />
      <circle cx={cx} r="9" className="cs-tf-comet-glow">
        <animate attributeName="cy" values="48;258;48" dur="3.4s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} r="4" className="cs-pv-solid">
        <animate attributeName="cy" values="48;258;48" dur="3.4s" repeatCount="indefinite" />
      </circle>
      {TF_STAGES.map((s, i) => (
        <g key={i} transform={`translate(${cx} ${TF_YS[i]})`}>
          <g className="cs-tf-stage" style={{ animationDelay: `${((TF_YS[i] - 48) / 210) * 1.7}s` }}>
            <circle r="21" className="cs-tf-bubble" />
            {tfRep(s.type)}
          </g>
          <text x="36" y="4" textAnchor="start" className="cs-pv-text cs-pv-text--sm">{s.label}</text>
        </g>
      ))}
    </svg>
  );
};

// Cost duel: glassy gradient bars grow inside faded track columns — a tall
// "human" bar and a tiny glowing "Siya" bar — under a pulsing "10×" badge.
const CompareVisual = () => (
  <svg className="cs-pv-svg cs-vs" viewBox="0 0 240 252">
    <defs>
      <linearGradient id="csvsFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0.6" />
      </linearGradient>
      <linearGradient id="csvsFillH" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.42" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0.16" />
      </linearGradient>
      <filter id="csvsGlow" x="-70%" y="-70%" width="240%" height="240%">
        <feGaussianBlur stdDeviation="7" />
      </filter>
    </defs>

    {/* badge — centred at top with breathing room */}
    <g className="cs-vs-badge">
      <rect x="70" y="14" width="100" height="28" rx="14" className="cs-vs-badge-bg" />
      <text x="120" y="32" textAnchor="middle" className="cs-vs-badge-text">10× CHEAPER</text>
    </g>

    {/* gridlines */}
    <line x1="38" y1="104" x2="202" y2="104" className="cs-vs-grid" />
    <line x1="38" y1="140" x2="202" y2="140" className="cs-vs-grid" />
    <line x1="38" y1="176" x2="202" y2="176" className="cs-vs-grid" />

    {/* tracks */}
    <rect x="44" y="68" width="54" height="130" rx="14" className="cs-vs-track" />
    <rect x="142" y="68" width="54" height="130" rx="14" className="cs-vs-track" />

    {/* human */}
    <rect x="44" y="72" width="54" height="126" rx="14" fill="url(#csvsFillH)" className="cs-vs-bar" />
    <text x="71" y="220" textAnchor="middle" className="cs-pv-text cs-pv-text--sm">Human</text>
    <text x="71" y="238" textAnchor="middle" className="cs-vs-val">₹30–50</text>

    {/* siya — winner, with glow */}
    <rect x="146" y="183" width="46" height="15" rx="7" fill="#ffffff" className="cs-vs-glow" filter="url(#csvsGlow)" />
    <rect x="142" y="183" width="54" height="15" rx="7" fill="url(#csvsFill)" className="cs-vs-bar cs-vs-bar--siya" />
    <text x="169" y="220" textAnchor="middle" className="cs-pv-text cs-pv-text--sm">Siya</text>
    <text x="169" y="238" textAnchor="middle" className="cs-vs-val cs-vs-val--win">₹2–3</text>
  </svg>
);

const GridVisual = ({ count }) => (
  <div className="cs-pv-grid">
    {Array.from({ length: Math.min(count, 6) }).map((_, i) => (
      <span key={i} className="cs-pv-cell" style={{ animationDelay: `${i * 0.18}s` }}>✓</span>
    ))}
  </div>
);

// Tech stack as a lively marquee: rows of pills drift in alternating directions
// with faded edges — a modern tech-stack ticker.
const PillsVisual = ({ tags }) => {
  const rows = [[], [], []];
  tags.forEach((t, i) => rows[i % 3].push(t));
  return (
    <div className="cs-stack">
      {rows.map((row, r) => (
        <div className="cs-stack-row" key={r}>
          <div className={`cs-stack-track ${r % 2 ? "cs-stack-track--rev" : ""}`}>
            {[...row, ...row, ...row].map((t, i) => (
              <span className="cs-pv-pill" key={i}>{t}</span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Radial voice-visualiser orb: a glowing core with a slowly-rotating ring of
// audio bars pulsing like a live voice, plus expanding sound ripples.
const VOICE_BARS = 40;
const CallVisual = () => (
  <svg className="cs-pv-svg cs-voice" viewBox="0 0 260 230">
    {/* live status chip */}
    <rect x="100" y="4" width="60" height="22" rx="11" className="cs-call-chip" />
    <circle cx="116" cy="15" r="3.4" className="cs-call-live" />
    <text x="125" y="19" className="cs-call-livetext">LIVE</text>

    {/* expanding sound ripples */}
    <circle cx="130" cy="126" r="46" className="cs-ripple" style={{ animationDelay: "0s" }} />
    <circle cx="130" cy="126" r="46" className="cs-ripple" style={{ animationDelay: "1.2s" }} />

    {/* soft halo */}
    <circle cx="130" cy="126" r="60" className="cs-call-glow" />

    {/* rotating radial waveform */}
    <g className="cs-voice-ring">
      {Array.from({ length: VOICE_BARS }).map((_, i) => (
        <g key={i} transform={`rotate(${i * (360 / VOICE_BARS)} 130 126)`}>
          <rect
            x="128.7"
            y="70"
            width="2.6"
            height="22"
            rx="1.3"
            className="cs-voice-bar"
            style={{ animationDelay: `${(i % 20) * 0.06}s` }}
          />
        </g>
      ))}
    </g>

    {/* glowing core */}
    <circle cx="130" cy="126" r="34" className="cs-voice-core" />
    <circle cx="130" cy="126" r="34" className="cs-voice-core cs-voice-core--pulse" />
    <path d="M130 116 v20 M120 120 v12 M140 120 v12 M112 124 v4 M148 124 v4" className="cs-voice-eq" />
  </svg>
);

const PinVisual = ({ label, project }) => {
  // A project's own modules bring their own visuals.
  const mod = project.modules?.find((m) => m.label === label);
  if (mod && MODULE_VISUALS[mod.visual]) {
    const ModuleVisual = MODULE_VISUALS[mod.visual];
    return <ModuleVisual />;
  }

  switch (label) {
    case "See it in action":
      return <CallVisual />;
    case "How it works":
      return <FlowVisual />;
    case "vs. Humans":
      return <CompareVisual />;
    case "Highlights":
      return <GridVisual count={project.features.length} />;
    case "Stack":
      return <PillsVisual tags={project.tags} />;
    default:
      // The phone mockup is Siya-specific — everything else gets the
      // neutral panel stack.
      return project.slug === "ai-voice-calling-agent" ? (
        <OrbitVisual />
      ) : (
        <LayersVisual />
      );
  }
};

const rise = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
};

/* Features written as "Title — description" split into card title + body. */
const splitFeature = (text) => {
  const i = text.indexOf("—");
  if (i > 6 && i < text.length - 6) {
    return [text.slice(0, i).trim(), text.slice(i + 1).trim()];
  }
  return [null, text];
};

/* ── How it works — voice agent case study only ── */
const voiceSteps = [
  {
    title: "Call comes in",
    body: "A student dials the institute's number. Plivo picks up the PSTN call and opens a WebSocket to the FastAPI server, streaming raw mulaw audio in real time.",
  },
  {
    title: "Three coroutines boot",
    body: "Every call runs three asyncio coroutines concurrently — a Plivo pump streaming audio in, a Deepgram pump handling transcription, and a turn runner orchestrating the response.",
  },
  {
    title: "Speech becomes text",
    body: "Deepgram transcribes the caller as they speak. The moment a speech_final event fires, the turn is handed to the LLM — typically in under 300 ms.",
  },
  {
    title: "The LLM decides",
    body: "GPT-4o-mini generates the reply with full conversation context. When it detects intent, it calls tools instead of just talking:",
    tools: [
      { name: "book_lead()", desc: "logs the lead to SQLite and pushes it to Google Sheets" },
      { name: "transfer_call()", desc: "bridges the caller to a human counselor via Plivo REST" },
    ],
  },
  {
    title: "Voice goes back out",
    body: "The reply is split into sentences. Each one is synthesized by Sarvam AI in under 200 ms and streamed back while the LLM is still writing the next — the full loop stays under a second.",
  },
];

const StepFlow = ({ steps }) => (
  <div className="cs-steps">
    {steps.map((step, i) => (
      <div className="cs-step" key={i}>
        <span className="cs-step-num">{String(i + 1).padStart(2, "0")}</span>
        <div className="cs-step-body">
          <h3 className="cs-step-title">{step.title}</h3>
          <p className="cs-step-desc">{step.body}</p>
          {step.tools && (
            <div className="cs-step-tools">
              {step.tools.map((t) => (
                <div className="cs-step-tool" key={t.name}>
                  <code>{t.name}</code>
                  <span>{t.desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

/* ── Comparison — voice agent case study only ── */
const compareRows = [
  { label: "Cost per minute", human: "₹30–50", siya: "₹2–3" },
  { label: "Availability", human: "9 AM – 6 PM", siya: "24 / 7" },
  { label: "Missed calls", human: "Yes, off-hours", siya: "Zero" },
  { label: "Languages", human: "One per agent", siya: "Auto-detects both" },
  { label: "Lead capture", human: "Manual entry", siya: "Automatic, to Sheets" },
  { label: "Response time", human: "Hold music", siya: "Under a second" },
];

const ComparisonTable = () => (
  <div className="cs-table">
    <div className="cs-table-row cs-table-head">
      <span />
      <span>Human call center</span>
      <span>Siya</span>
    </div>
    {compareRows.map((row) => (
      <div className="cs-table-row" key={row.label}>
        <span className="cs-table-label">{row.label}</span>
        <span className="cs-table-human">{row.human}</span>
        <span className="cs-table-siya">{row.siya}</span>
      </div>
    ))}
  </div>
);

const ProjectDetail = ({ slug, onBack, onOpenProject }) => {
  const project = getProjectBySlug(slug);
  const nextProject = getNextProject(slug);
  const progressRef = useRef(null);
  const sectionRefs = useRef([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
  }, [slug]);

  // Scrollytelling: mark the section crossing the viewport's middle band as active.
  useEffect(() => {
    const els = sectionRefs.current.filter(Boolean);
    if (!els.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(Number(e.target.dataset.idx));
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [slug]);

  // Thin reading-progress bar tied to scroll position.
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const max = el.scrollHeight - el.clientHeight;
      const p = max > 0 ? el.scrollTop / max : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${p})`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  if (!project) {
    return (
      <div className="cs-page">
        <div className="cs-container">
          <h1 className="cs-title">Not found</h1>
          <p className="cs-subtitle">This project doesn't exist.</p>
        </div>
      </div>
    );
  }

  const isVoiceAgent = project.slug === "ai-voice-calling-agent";
  const [intro, ...restParas] = project.longDescription;

  // Numbered content sections — conditional ones only appear for the voice agent.
  const sections = [
    {
      label: "Overview",
      body: restParas.map((para, i) => (
        <p className="cs-body" key={i}>{para}</p>
      )),
    },
    ...(isVoiceAgent
      ? [{
          label: "See it in action",
          body: (
            <>
              <p className="cs-body">
                A live walkthrough of Siya handling a real call — hear how she
                greets the caller, answers questions, and captures the lead.
              </p>
              <div className="cs-demo">
                <video
                  src="/voice-agent-demo.mp4"
                  poster="/voice-agent-demo-poster-v2.jpg"
                  controls
                  playsInline
                  preload="none"
                />
              </div>
            </>
          ),
        }]
      : []),
    // One section per module, each with its own demonstrating visual and an
    // optional demo video sitting under the copy that explains it.
    ...(project.modules || []).map((m) => ({
      label: m.label,
      body: (
        <>
          <p className="cs-body">{m.body}</p>
          {m.stepsIntro && <p className="cs-body">{m.stepsIntro}</p>}
          {m.steps && <StepFlow steps={m.steps} />}
          {m.demo && (
            <div className="cs-demo">
              <video
                src={m.demo.src}
                poster={m.demo.poster}
                controls
                playsInline
                preload="none"
              />
            </div>
          )}
        </>
      ),
    })),
    // Highlights would just restate the modules, so skip it when they exist.
    ...(project.modules
      ? []
      : [{
          label: "Highlights",
          body: (
            <div className="cs-cards">
              {project.features.map((feature, i) => {
                const [title, bodyText] = splitFeature(feature);
                return (
                  <div className="cs-card" key={i}>
                    {title && <h3 className="cs-card-title">{title}</h3>}
                    <p className="cs-card-body">{bodyText}</p>
                  </div>
                );
              })}
            </div>
          ),
        }]),
    ...(isVoiceAgent
      ? [{
          label: "How it works",
          body: (
            <>
              <p className="cs-body">
                From the caller's first word to Siya's reply, the whole loop
                runs in under a second. Here is what happens on every call:
              </p>
              <StepFlow steps={voiceSteps} />
            </>
          ),
        }]
      : []),
    ...(isVoiceAgent
      ? [{
          label: "vs. Humans",
          body: (
            <>
              <p className="cs-body">
                The institute was missing every call outside office hours. Siya
                answers all of them, in both languages, at a tenth of the cost.
              </p>
              <ComparisonTable />
            </>
          ),
        }]
      : []),
    {
      label: "Stack",
      body: (
        <div className="cs-tags">
          {project.tags.map((tag) => (
            <span className="cs-tag" key={tag}>{tag}</span>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div className="cs-page" key={slug} style={{ "--accent": project.accent }}>
      <div className="cs-progress">
        <span className="cs-progress-fill" ref={progressRef} />
      </div>
      <div className="cs-container">
        {/* Back */}
        <button className="cs-back" onClick={onBack}>
          <span className="cs-back-arrow">←</span> All projects
        </button>

        {/* Hero */}
        <motion.header
          className="cs-hero"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="cs-meta">
            <span>{project.role}</span>
            <span className="cs-meta-dot">·</span>
            <span>{project.year}</span>
          </div>
          <h1 className="cs-title">{project.title}</h1>
          <p className="cs-subtitle">{project.subtitle}</p>
          <p className="cs-intro">{intro}</p>
          <div className="cs-tags">
            {project.tags.map((tag) => (
              <span className="cs-tag" key={tag}>{tag}</span>
            ))}
          </div>
        </motion.header>

        {/* Stat band */}
        {project.metrics && (
          <motion.div className="cs-stats" {...rise}>
            {project.metrics.map((m) => (
              <div className="cs-stat" key={m.label}>
                <span className="cs-stat-value">{m.value}</span>
                <span className="cs-stat-label">{m.label}</span>
              </div>
            ))}
          </motion.div>
        )}

        {/* Scrollytelling: pinned visual + scrolling narrative */}
        <div className="cs-scrolly">
          {/* Pinned panel — reacts to the active section */}
          <div className="cs-pin">
            <div className="cs-pin-panel" style={{ background: project.gradient }}>
              <span className="cs-pin-project">{project.title}</span>

              <div className="cs-pin-stage">
                <AnimatePresence mode="wait">
                  <motion.div
                    className="cs-pin-visual"
                    key={active}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.03 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <PinVisual label={sections[active].label} project={project} />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="cs-pin-foot">
                <AnimatePresence mode="wait">
                  <motion.div
                    className="cs-pin-body"
                    key={active}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <span className="cs-pin-label">{sections[active].label}</span>
                  </motion.div>
                </AnimatePresence>
                <div className="cs-pin-dots">
                  {sections.map((s, i) => (
                    <span key={s.label} className={`cs-pin-dot ${i === active ? "on" : ""}`} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Scrolling narrative */}
          <div className="cs-story">
            {sections.map((s, i) => (
              <section
                className="cs-story-section"
                key={s.label}
                data-idx={i}
                ref={(el) => (sectionRefs.current[i] = el)}
              >
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="cs-story-head">
                    <span className="cs-story-num">{String(i + 1).padStart(2, "0")}</span>
                    <h2 className="cs-story-label">{s.label}</h2>
                  </div>
                  <div className="cs-story-col">{s.body}</div>
                </motion.div>
              </section>
            ))}
          </div>
        </div>

        {/* Gallery — only when real images exist */}
        {project.gallery.length > 0 && (
          <motion.section className="cs-section" {...rise}>
            <div className="cs-gallery">
              {project.gallery.map((src, i) => (
                <div className="cs-gallery-item" key={i}>
                  <img src={src} alt={`${project.title} — ${i + 1}`} />
                </div>
              ))}
            </div>
          </motion.section>
        )}

        {/* Next project */}
        <motion.section className="cs-next-wrap" {...rise}>
          <span className="cs-next-label">Next project</span>
          <button className="cs-next" onClick={() => onOpenProject(nextProject.slug)}>
            {nextProject.title}
            <span className="cs-next-arrow">→</span>
          </button>
        </motion.section>

        <footer className="cs-footer">
          <span>© {new Date().getFullYear()} Sanskar Lalawat</span>
          <span>AI Engineer — India</span>
        </footer>
      </div>
    </div>
  );
};

export default ProjectDetail;
